import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userCan } from '../config/permissions';

/**
 * Hook for runtime permission checks inside components.
 *
 *   const can = useCan();
 *   if (can('can_delete_lots')) { … }
 */
export function useCan() {
  const { user } = useAuth();
  return (key) => userCan(user, key);
}


/**
 * Page-level guard. Redirects to /dashboard if the user lacks permission.
 *
 *   <RequirePermission perm="cashflow"><CashflowPage /></RequirePermission>
 *
 * Owners/admins always pass.
 */
export function RequirePermission({ perm, fallback = '/dashboard', children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!userCan(user, perm)) return <Navigate to={fallback} replace />;
  return children;
}


/**
 * Inline component guard — render `null` (or `fallback`) when the user lacks
 * the permission. Useful for hiding buttons/sections inside otherwise-allowed
 * pages.
 *
 *   <Can perm="can_delete_lots"><DeleteButton /></Can>
 */
export function Can({ perm, fallback = null, children }) {
  const { user } = useAuth();
  return userCan(user, perm) ? children : fallback;
}
